import {FC} from 'react';
import {useNavigate} from 'react-router';

import Page from '@/components/Page';

import {useCreateRateSheet} from '../providers/createRateSheetProvider';

/**
 * Rate sheet details component
 * @returns {JSX.Element}
 */
const RateSheetDetailsComponent: FC = () => {
  const navigate = useNavigate();
  const {rates, acknowledged, setAcknowledged, setAcknowledging, handleSubmitRateSheet, isSubmitting, mode, title} =
    useCreateRateSheet();

  const handleSubmit = async () => {
    if (acknowledged) {
      try {
        await handleSubmitRateSheet();
        navigate('/distributor/rate-sheets');
      } catch (error) {
        console.error('Error submitting rate sheet:', error);
      }
    }
  };

  return (
    <Page
      handleBack={() => {
        if (mode === 'view') {
          navigate('/distributor/rate-sheets');
        } else {
          setAcknowledging(false);
        }
      }}
      currentPage="Review Rate Sheet"
      activePage="Review Rate Sheet"
      mainPage="Review Rate Sheet"
    >
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12">
          <div className="box">
            <div className="box-header block md:flex">
              <div className="bllock h5 mb-0 items-center sm:flex">
                <img className="w-10" src="/images/logo/logo.png" alt="" />
                <div className="ms-0 mt-2 sm:ms-2 sm:mt-0">
                  <div className="h6 mb-0 font-semibold">
                    RATE SHEET REVIEW : <span className="text-primary">{title}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="box-body">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 xl:col-span-12">
                  <div className="table-responsive">
                    <table className="nowrap mt-4 table min-w-full whitespace-nowrap border dark:border-defaultborder/10">
                      <thead>
                        <tr>
                          <th scope="row" className="text-start">
                            Product Name
                          </th>
                          <th scope="row" className="text-start">
                            Brand Name
                          </th>
                          <th scope="row" className="text-start">
                            Product Category
                          </th>
                          <th scope="row" className="text-start">
                            Product Sub Category
                          </th>
                          <th scope="row" className="text-start">
                            Wholesale Price
                          </th>
                          <th scope="row" className="text-start">
                            TNC Price
                          </th>
                          <th scope="row" className="text-start">
                            Min Quantity
                          </th>

                          <th scope="row" className="text-start">
                            Factored Price
                          </th>

                          <th scope="row" className="text-start">
                            MSRP
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rates.map((rate, index) => (
                          <tr
                            key={`${rate.fk_product_id}-${index}`}
                            className="border border-defaultborder dark:border-defaultborder/10"
                          >
                            {/* PRODUCT NAME */}
                            <td className="flex min-w-[150px] items-center gap-2">
                              {/* display image if image is available */}
                              {rate.Product?.product_image_url && (
                                <img
                                  src={rate.Product?.product_image_url}
                                  alt={rate.Product?.product_name || 'Unknown Product'}
                                  className="w-10"
                                />
                              )}
                              <div className="font-semibold">{rate.Product?.product_name || 'Unknown Product'}</div>
                            </td>
                            {/* BRAND NAME */}
                            <td>
                              <div className="text-[#8c9097] dark:text-white/50">
                                {rate.Brand?.brand_name || 'Unknown Brand'}
                              </div>
                            </td>
                            {/* CATEGORY */}
                            <td>
                              <div className="text-[#8c9097] dark:text-white/50">
                                {rate.Product?.product_category || 'Unknown Category'}
                              </div>
                            </td>
                            {/* SUB CATEGORY */}
                            <td className="product-quantity-container">
                              {rate.Product?.product_subcategory || 'Unknown Sub Category'}
                            </td>
                            {/* WHOLESALE PRICE */}
                            <td>
                              $
                              {rate.wholesale_price?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            {/* TNC PRICE */}
                            <td>
                              $
                              {rate.tnc_price?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            {/* MIN QUANTITY */}
                            <td>{rate.min_quantity}</td>
                            {/* FACTORED PRICE */}
                            <td>
                              {rate.factored_price ? (
                                <>
                                  $
                                  {rate.factored_price?.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            {/* MSRP*/}
                            <td>
                              {rate.msrp ? (
                                <>
                                  $
                                  {rate.msrp?.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {mode !== 'view' && (
              <div className="box-footer flex flex-col items-start justify-between space-y-4 md:flex-row md:items-center md:space-y-0">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="terms"
                    className="mr-2"
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                  />
                  <label htmlFor="terms" className="text-sm">
                    I acknowledge and agree to the terms and conditions
                  </label>
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => setAcknowledging(false)} type="button" className="ti-btn bg-danger text-white">
                    Cancel <i className="ri-close-line ms-1 align-middle"></i>
                  </button>
                  <button
                    type="button"
                    className={`ti-btn ${acknowledged && !isSubmitting ? 'bg-success text-white' : 'cursor-not-allowed bg-gray-400 text-gray-600'}`}
                    disabled={!acknowledged || isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'} <i className="ri-check-line ms-1 align-middle"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default RateSheetDetailsComponent;
